/**
 * IconAvatar — renders a layered character portrait.
 *
 * Backwards-compatible: legacy characters carry just { set, id, hue }
 * which we render as the original glyph-on-disc avatar. New characters
 * carry additional layer fields (body, hat, face, accentHue) which we
 * stack into a richer portrait: body silhouette + face + optional hat
 * + accent hue tint.
 *
 * Phase E1 — added optional `border` and `background` cosmetic slots.
 * Z-order back→front: background → body → face → hat → border ring
 * overlay. Both default to `undefined` ⇒ render nothing (legacy avatars
 * stay visually unchanged).
 */
import { ICON_PATHS } from './iconPaths';
import {
  BODY_SHAPES,
  HAT_SHAPES,
  FACE_SHAPES,
  BORDERS,
  BACKGROUNDS,
  MASKS,
  type CharacterIconExt,
} from './characterParts';

// Palette table — mirrors PALETTES in @tt/character-render. Kept in sync
// manually because importing the Pixi-rendering package into the React/SVG
// portrait path would pull pixi.js side-imports into the lobby bundle.
// When you add a palette here, also add it in shared/character-render.
interface PaletteHSL {
  body: { h: number; s: number; l: number };
  accent: { h: number; s: number; l: number };
  features: { h: number; s: number; l: number };
  animated?: boolean;
}
const PALETTE_HSL: Record<string, PaletteHSL> = {
  'palette-dusk':      { body: { h: 280, s: 70, l: 36 }, accent: { h: 340, s: 78, l: 62 }, features: { h: 50,  s: 62, l: 72 } },
  'palette-bone':      { body: { h: 40,  s: 14, l: 86 }, accent: { h: 220, s: 12, l: 56 }, features: { h: 220, s: 18, l: 28 } },
  'palette-emerald':   { body: { h: 150, s: 76, l: 38 }, accent: { h: 50,  s: 88, l: 60 }, features: { h: 130, s: 32, l: 78 } },
  'palette-prismatic': { body: { h: 0,   s: 80, l: 56 }, accent: { h: 50,  s: 88, l: 60 }, features: { h: 240, s: 28, l: 18 }, animated: true },
  // Themed set palettes — KEEP IN SYNC with character-render + shell-3d.
  'palette-steel':     { body: { h: 216, s: 26, l: 45 }, accent: { h: 208, s: 30, l: 72 }, features: { h: 220, s: 26, l: 24 } },
  'palette-arcane':    { body: { h: 262, s: 58, l: 40 }, accent: { h: 188, s: 82, l: 60 }, features: { h: 270, s: 38, l: 82 } },
  'palette-royal':     { body: { h: 278, s: 52, l: 32 }, accent: { h: 45,  s: 82, l: 56 }, features: { h: 44,  s: 46, l: 86 } },
  'palette-naval':     { body: { h: 214, s: 52, l: 30 }, accent: { h: 42,  s: 72, l: 55 }, features: { h: 40,  s: 22, l: 88 } },
};

export interface IconValue {
  set: string;
  id: number;
  hue: number;
  body?: number;
  hat?: number;
  face?: number;
  accentHue?: number;
  /** Optional 3-color palette id (Phase 2). When set, BOTH the lobby
   *  IconAvatar SVG (ExtendedAvatar AND LegacyAvatar) and the in-game
   *  Pixi renderer resolve body/accent/feature fills from
   *  PALETTE_HSL[paletteId] instead of the hue+accentHue derivation.
   *  An unknown id falls back to hue colors (never default-gray). Keep
   *  PALETTE_HSL above in sync with PALETTES in @tt/character-render. */
  paletteId?: string;
  /** Avatar-frame cosmetic id (Phase E1). Looked up in BORDERS registry. */
  border?: string;
  /** Avatar-backdrop cosmetic id (Phase E1). Looked up in BACKGROUNDS registry. */
  background?: string;
  /** Phase F5 — face-overlay mask. Looked up in MASKS registry. */
  mask?: string;
  /** Body-outfit cosmetic id (clothing / armor on the figure's torso /
   *  arms / legs). 3D-only — the 2D avatar portrait does not render it.
   *  Looked up in OUTFITS registry. */
  outfit?: string;
  /** Ambient particle cosmetic id (3D drifting motes around the figure).
   *  Unified "Particles" Flair family — covers the avatar `particles` kind
   *  and the (retired-as-2D) seat-particle family. 3D-only showcase. */
  particles?: string;
  /** Colored rim/stand-glow hue (0–359) under the figure's feet in 3D.
   *  Unified "Glow" Flair family — repurposed seat-bghue. 3D-only showcase. */
  rimHue?: number;
}

interface Props {
  icon: IconValue;
  size?: number;
  alt?: string | undefined;
}

export function IconAvatar({ icon, size = 72, alt }: Props) {
  const isExtended = typeof icon.body === 'number';
  if (isExtended) {
    return <ExtendedAvatar icon={icon as CharacterIconExt} size={size} {...(alt !== undefined ? { alt } : {})} />;
  }
  return <LegacyAvatar icon={icon} size={size} {...(alt !== undefined ? { alt } : {})} />;
}

function LegacyAvatar({ icon, size = 72, alt }: { icon: IconValue; size?: number; alt?: string | undefined }) {
  const path = ICON_PATHS[icon.id] ?? ICON_PATHS[0];
  // Honor an equipped palette on legacy {set,id,hue} avatars too: when a
  // KNOWN paletteId is set, the disc fill derives from the palette body
  // color; otherwise it derives from hue. An unknown id falls back to
  // hue (never default-gray) since `palette` stays undefined.
  const palette = icon.paletteId ? PALETTE_HSL[icon.paletteId] : undefined;
  const baseColor = palette
    ? `hsl(${palette.body.h} ${palette.body.s}% ${Math.min(72, palette.body.l + 20)}%)`
    : `hsl(${(20 + icon.hue) % 360} 70% 56%)`;
  const ringColor = palette
    ? `hsl(${palette.body.h} ${Math.max(30, palette.body.s - 10)}% ${Math.max(18, palette.body.l - 4)}%)`
    : `hsl(${(20 + icon.hue) % 360} 60% 32%)`;
  return (
    <span
      role={alt ? 'img' : undefined}
      aria-label={alt}
      className="icon-avatar"
      style={{
        width: size,
        height: size,
        background: `radial-gradient(circle at 35% 30%, ${baseColor} 0%, ${ringColor} 100%)`,
      }}
    >
      <svg width={size * 0.62} height={size * 0.62} viewBox="0 0 24 24" aria-hidden="true">
        <path d={path} fill="#1A1410" stroke="#1A1410" strokeWidth="0.4" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function ExtendedAvatar({ icon, size, alt }: { icon: CharacterIconExt; size: number; alt?: string | undefined }) {
  const bodyIdx = icon.body ?? 0;
  const faceIdx = icon.face ?? 0;
  const body = BODY_SHAPES[bodyIdx % BODY_SHAPES.length]!;
  const hatIdx = icon.hat ?? 0;
  const hat = HAT_SHAPES[hatIdx % HAT_SHAPES.length]!;
  const face = FACE_SHAPES[faceIdx % FACE_SHAPES.length]!;

  const palette = icon.paletteId ? PALETTE_HSL[icon.paletteId] : undefined;

  const fallbackBodyHue = icon.hue;
  const fallbackAccentHue = icon.accentHue ?? ((icon.hue + 60) % 360);

  const bodyHue   = palette ? palette.body.h     : fallbackBodyHue;
  const bodyS     = palette ? palette.body.s     : 70;
  const bodyL     = palette ? palette.body.l     : 56;
  const accentH   = palette ? palette.accent.h   : fallbackAccentHue;
  const accentS   = palette ? palette.accent.s   : 75;
  const accentL   = palette ? palette.accent.l   : 60;
  const featuresH = palette ? palette.features.h : (fallbackAccentHue);
  const featuresS = palette ? palette.features.s : 28;
  const featuresL = palette ? palette.features.l : 18;

  const bgFill = `hsl(${bodyHue} ${Math.max(20, bodyS - 10)}% ${Math.max(8, bodyL - 38)}%)`;
  const bgFillDark = `hsl(${bodyHue} ${Math.max(30, bodyS)}% ${Math.max(4, bodyL - 48)}%)`;
  const bodyHi = `hsl(${bodyHue} ${Math.min(95, bodyS + 10)}% ${Math.min(80, bodyL + 14)}%)`;
  const bodyShadow = `hsl(${bodyHue} ${Math.max(40, bodyS - 5)}% ${Math.max(8, bodyL - 18)}%)`;
  const accentFill = `hsl(${accentH} ${accentS}% ${accentL}%)`;
  const featureFill = `hsl(${featuresH} ${featuresS}% ${featuresL}%)`;

  const gradId = `bodyG-${icon.paletteId ?? `h${bodyHue}`}-${bodyIdx}`;
  const animated = palette?.animated === true;

  // Phase E1 — border + background overlays. Both default to "render
  // nothing" so legacy avatars without these fields look identical to
  // before. Each registry entry's `render` returns SVG fragments + an
  // optional className that drives CSS animation. The render functions
  // return data declaratively (SVG element trees) rather than mutating
  // a DOM node — keeps the React render pure and avoids needing a ref
  // / useEffect dance per portrait.
  const bgEntry = icon.background ? BACKGROUNDS.find((b) => b.id === icon.background) : undefined;
  const borderEntry = icon.border ? BORDERS.find((b) => b.id === icon.border) : undefined;
  // Phase F5 — mask is a face-overlay layer drawn between face and hat.
  const maskEntry = icon.mask ? MASKS.find((m) => m.id === icon.mask) : undefined;
  // Phase D pass-2 — each layer gets a uid suffix so multi-avatar pages
  // (post-round screen) don't collide on internal <defs> ids when
  // multiple wearers share the same border/background/mask. The uid is
  // built from gradId (palette+bodyIdx) plus a per-slot tag.
  const bgOpts = { primaryHue: bodyHue, accentHue: accentH, uid: `bg-${gradId}-${icon.background ?? ''}` };
  const borderOpts = { primaryHue: bodyHue, accentHue: accentH, uid: `br-${gradId}-${icon.border ?? ''}` };
  const maskOpts = { primaryHue: bodyHue, accentHue: accentH, uid: `mk-${gradId}-${icon.mask ?? ''}` };
  const bgRender = bgEntry?.render(bgOpts);
  const borderRender = borderEntry?.render(borderOpts);
  const maskRender = maskEntry?.render(maskOpts);

  // Phase D — Tier 4 rich render: per-cosmetic richSvg fragments that
  // fully replace the primitive single-path render when present. We
  // construct a unique uid per cosmetic-on-this-avatar so concurrent
  // avatars wearing the same item don't ID-collide on the gradient /
  // filter ids inside the fragment. The uid uses gradId (which already
  // mixes palette + bodyIdx) plus the cosmetic slot tag.
  const richOpts = {
    primaryHue: bodyHue,
    accentHue: accentH,
    bodyHi,
    bodyShadow,
    accentFill,
    featureFill,
    bgFill,
    bgFillDark,
    uid: '',
  };
  const bodyRich = body.richSvg ? body.richSvg({ ...richOpts, uid: `b-${gradId}-${bodyIdx}` }) : null;
  const hatRich = hat.richSvg && hatIdx > 0 ? hat.richSvg({ ...richOpts, uid: `h-${gradId}-${hatIdx}` }) : null;
  const faceRich = face.richSvg ? face.richSvg({ ...richOpts, uid: `f-${gradId}-${faceIdx}` }) : null;

  return (
    <span
      role={alt ? 'img' : undefined}
      aria-label={alt}
      className={`icon-avatar${animated ? ' icon-avatar-animated' : ''}`}
      style={{
        width: size,
        height: size,
        background: `radial-gradient(circle at 35% 30%, ${bgFill} 0%, ${bgFillDark} 100%)`,
      }}
    >
      <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
        <defs>
          <radialGradient id={gradId} cx="40%" cy="35%" r="65%">
            <stop offset="0%" stopColor={bodyHi} />
            <stop offset="100%" stopColor={bodyShadow} />
          </radialGradient>
          {/* Circular clip so background cosmetics that paint to the
           *  full 100x100 viewBox (e.g. radial gradients, starfield
           *  rects) don't bleed past the round avatar frame. The
           *  parent <span> uses `border-radius: 50%` for visual
           *  rounding, but the SVG itself doesn't inherit that —
           *  background rects show as squares without this clip. */}
          <clipPath id={`${gradId}-bgclip`}>
            <circle cx="50" cy="50" r="50" />
          </clipPath>
        </defs>
        {/* Background layer (Phase E1) — fills the 100x100 frame BEHIND
            the body. Clipped to the avatar circle. Renders nothing
            when icon.background is undefined. */}
        {bgRender && (
          <g className={bgRender.className} clipPath={`url(#${gradId}-bgclip)`}>{bgRender.children}</g>
        )}
        {/* Body — Phase D Tier 4 richSvg replaces the primitive render
            when present. Items without richSvg use the legacy path
            silhouette + decoration. */}
        {bodyRich ? (
          <g>{bodyRich}</g>
        ) : (
          <g>
            <path
              d={body.body}
              fill={`url(#${gradId})`}
              stroke={bodyShadow}
              strokeWidth="0.6"
            />
            {/* Phase E3 — optional body decoration (Solstice ember etc.).
                Painted in fixed decorationFill if provided, else accent.
                Wrapped in a class-tagged <g> so CSS keyframes can animate. */}
            {body.decorationPath && (
              <g className={body.decorationClass}>
                <path
                  d={body.decorationPath}
                  fill={body.decorationFill ?? accentFill}
                />
              </g>
            )}
          </g>
        )}
        {/* Face features — Phase D Tier 4 richSvg replaces the primitive
            eyes+mouth+mark render when present. */}
        {faceRich ? (
          <g>{faceRich}</g>
        ) : (
          <>
            <g className={face.eyeClass}>
              {face.eyes.map((e, i) => (
                <circle key={`eye-${i}`} cx={e.x} cy={e.y} r={e.r} fill={face.eyeFill ?? featureFill} />
              ))}
            </g>
            <g>
              {face.mouth && (
                <path d={face.mouth} fill="none" stroke={featureFill} strokeWidth="1.6" strokeLinecap="round" />
              )}
            </g>
            {/* Phase E3 — optional face mark (e.g. Searing Mark vertical scar). */}
            {face.markPath && (
              <path d={face.markPath} fill={face.markFill ?? featureFill} />
            )}
            {/* Cheek blush */}
            {face.blush && (
              <g opacity="0.5">
                <circle cx={face.blush[0]} cy={face.blush[1]} r="3" fill={accentFill} />
                <circle cx={face.blush[2]} cy={face.blush[1]} r="3" fill={accentFill} />
              </g>
            )}
          </>
        )}
        {/* Phase F5 — face-overlay mask. Drawn ON TOP of face features
            but UNDER the hat so a hat with a brim still occludes the
            top of the mask correctly. Render fn returns SVG fragment +
            optional className for CSS animation (cosmetic-animations
            section in global.css). */}
        {maskRender && (
          <g className={maskRender.className}>{maskRender.children}</g>
        )}
        {/* Hat — Phase D Tier 4 richSvg replaces the primitive
            path+accent render when present. */}
        {hatIdx > 0 && hat && (hatRich ? (
          <g>{hatRich}</g>
        ) : (
          <g>
            <path
              d={hat.path}
              fill={hat.baseFill ?? accentFill}
              stroke="#0a0814"
              strokeWidth="0.8"
            />
            {hat.accent && (
              <g className={hat.accentClass}>
                <path d={hat.accent} fill={hat.accentFill ?? '#0a0814'} />
              </g>
            )}
          </g>
        ))}
        {/* Border ring overlay (Phase E1) — drawn AFTER all other layers
            so it always reads as a frame around the avatar. Renders
            nothing when icon.border is undefined. */}
        {borderRender && (
          <g className={borderRender.className}>{borderRender.children}</g>
        )}
      </svg>
    </span>
  );
}
